import { IObservableArray, action, computed, flow, makeAutoObservable, observable } from 'mobx';
import type ExplorerRootStore from '../ExplorerRootStore';
import { BaseConnector } from './Base';
import SuiWallet from './Wallets/SuiWallet';
import {
	getWallets,
	isWalletWithRequiredFeatureSet,
	Wallet as SuiAdapter,
	SuiWalletFeatures,
	WalletWithRequiredFeatures,
} from '@mysten/wallet-standard';
import { SuiClient } from '@mysten/sui/client';

const SIGN_FEATURES = ['sui:signTransaction', 'sui:signTransactionBlock'] satisfies (keyof SuiWalletFeatures)[];

const filter = (wallet: SuiAdapter): wallet is WalletWithRequiredFeatures =>
	isWalletWithRequiredFeatureSet(wallet) && SIGN_FEATURES.some((feature) => wallet.features[feature]);

export default class SUIConnector implements BaseConnector<SuiWallet> {
	private adaptors = getWallets();
	root: ExplorerRootStore;
	wallets: IObservableArray<SuiWallet> = observable([]);
	constructor(root: ExplorerRootStore) {
		this.root = root;

		makeAutoObservable<SUIConnector, 'adaptors' | 'register' | 'unregister'>(
			this,
			{
				adaptors: false,
				register: action.bound,
				unregister: action.bound,
				client: computed({ keepAlive: true }),
				lastUsed: observable.ref,
			},
			{ autoBind: true },
		);
		this.initializeWalelts();
	}

	private initializeWalelts = action(() => {
		this.wallets.replace(
			this.adaptors
				.get()
				.filter(filter)
				.map((w) => new SuiWallet(this, w)),
		);
		this.adaptors.on('register', this.register);
		this.adaptors.on('unregister', this.unregister);
	});

	get client() {
		const suiChain = this.root.configs.combinedChains.find((c) => c.nameId === 'sui');
		const rpcURL = suiChain?.externalRpcURL || suiChain?.rpcURL || 'https://rpc.mainnet.sui.io/';
		return new SuiClient({ url: rpcURL });
	}

	connect = flow(function* (this: SUIConnector, wallet: SuiWallet) {
		yield wallet.connect();
		this.lastUsed = wallet;
	});
	disconnect(wallet: SuiWallet): Promise<void> {
		return wallet.disconnect();
	}
	lastUsed: SuiWallet | undefined = undefined;

	private register(...wallets: SuiAdapter[]) {
		wallets.filter(filter).forEach((w) => {
			const current = this.wallets.find((suiw) => suiw.adapter.id === w.id && suiw.adapter.name === w.name);
			if (current) {
				current.dispose();
				this.wallets.remove(current);
			}
			this.wallets.push(new SuiWallet(this, w));
		});
	}

	private unregister(...wallets: SuiAdapter[]) {
		wallets.forEach((w) => {
			const current = this.wallets.find((suiw) => suiw.adapter.id === w.id && suiw.adapter.name === w.name);
			if (current) {
				current.dispose();
				this.wallets.remove(current);
			}
		});
	}
}
